/* Kookut feeding calculator (snippets/kookut-feeding-calculator.liquid).
   Energy basis: the kibble the pet eats today (grams x kcal of that kibble), or its weight
   (dogs: 80 / 95 / 110 kcal x kg^0.75, cats: 85 / 95 / 115 kcal x kg^0.67: the FEDIAF formula,
   set to match the feeding tables on the Kookut kibble bags). Fillets or Terrine replace part of that energy; kibble is reduced to match. */

if (!customElements.get('kk-feed-calc')) {
  const OTHER_KCAL = 370; // fallback kcal per 100 g if no kibble data is found
  const FRACTIONS = { 0: '', 0.25: '¼', 0.5: '½', 0.75: '¾' };

  class KkFeedCalc extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      try {
        this.products = JSON.parse(this.querySelector('[data-kk-calc-data]').textContent) || [];
        this.t = JSON.parse(this.querySelector('[data-kk-strings]').innerHTML);
      } catch (e) {
        this.products = [];
        this.t = {};
      }
      this.products.forEach((p) => {
        const m = String(p.energy || '').match(/(\d+(?:[.,]\d+)?)\s*(?:kcal|calories)/i);
        p.kcal = m ? parseFloat(m[1].replace(',', '.')) : 0;
      });
      this.products = this.products.filter((p) => p.kcal > 0);

      this.money = new Intl.NumberFormat(this.dataset.locale || undefined, {
        style: 'currency',
        currency: this.dataset.currency || 'CHF',
      });

      const presetPet = this.dataset.presetPet;
      this.state = {
        pet: presetPet === 'dog' ? 'dog' : 'cat',
        useWeight: false,
        activity: 'normal',
        terrine: null, // share of the day as Terrine (0 = none, 1 = all)
        fillet: null, // tins of fillet per day (0 = none)
      };

      this.el = {
        kibble: this.querySelector('[data-kk-kibble]'),
        grams: this.querySelector('[data-kk-grams]'),
        weight: this.querySelector('[data-kk-weight]'),
        gramsWrap: this.querySelector('[data-kk-grams-wrap]'),
        weightWrap: this.querySelector('[data-kk-weight-wrap]'),
        terrineBlock: this.querySelector('[data-kk-terrine-block]'),
        terrineRecipe: this.querySelector('[data-kk-terrine-recipe]'),
        terrineAmounts: this.querySelector('[data-kk-terrine-amounts]'),
        filletRecipe: this.querySelector('[data-kk-fillet-recipe]'),
        filletAmounts: this.querySelector('[data-kk-fillet-amounts]'),
        lines: this.querySelector('[data-kk-lines]'),
        bar: this.querySelector('[data-kk-bar]'),
        legend: this.querySelector('[data-kk-legend]'),
        cost: this.querySelector('[data-kk-cost]'),
        note: this.querySelector('[data-kk-note]'),
      };

      this.querySelectorAll('[data-kk-pet]').forEach((b) =>
        b.addEventListener('click', () => {
          this.state.pet = b.dataset.kkPet;
          this.state.terrine = null;
          this.state.fillet = null;
          this.setup(true);
        })
      );
      this.querySelectorAll('[data-kk-activity]').forEach((b) =>
        b.addEventListener('click', () => {
          this.state.activity = b.dataset.kkActivity;
          this.render();
        })
      );
      this.querySelectorAll('[data-kk-toggle-weight]').forEach((b) =>
        b.addEventListener('click', () => {
          this.state.useWeight = !this.state.useWeight;
          this.render();
          (this.state.useWeight ? this.el.weight : this.el.grams).focus();
        })
      );
      this.el.kibble.addEventListener('change', () => this.render());
      this.el.terrineRecipe.addEventListener('change', () => this.render());
      this.el.filletRecipe.addEventListener('change', () => this.render());
      this.el.grams.addEventListener('input', () => this.render());
      this.el.weight.addEventListener('input', () => this.render());

      this.setup(false);
    }

    list(range, pet) {
      return this.products.filter((p) => p.range === range && p.pet === pet);
    }

    option(value, label) {
      const o = document.createElement('option');
      o.value = value;
      o.textContent = label;
      return o;
    }

    // Kibble choices for the pet (adult recipes first), then the Terrine and fillet recipes.
    setup(petChanged) {
      const { pet } = this.state;
      const presetId = Number(this.dataset.presetId);
      // Adult recipes first, then alphabetical (Chicken & Duck before Salmon & White Fish).
      const kibbles = this.list('kibble', pet).sort(
        (a, b) => /kitten|puppy/i.test(a.name) - /kitten|puppy/i.test(b.name) || a.name.localeCompare(b.name)
      );
      // Kookut kibble only: no "another brand" choice (we don't suggest pairing with other brands' food).
      this.el.kibble.replaceChildren(...kibbles.map((k) => this.option(k.id, k.name)));
      const presetKibble = kibbles.find((k) => k.id === presetId);
      this.el.kibble.value = presetKibble ? presetKibble.id : kibbles.length ? kibbles[0].id : '';

      // Start with a typical pet so a result shows straight away: 4 kg cat, 10 kg dog, normal activity.
      if (petChanged || !this.el.grams.value) {
        this.el.weight.value = pet === 'dog' ? 10 : 4;
        const kcal = this.kibbleKcal();
        this.el.grams.value = this.round5((this.energyFromWeight() / kcal) * 100);
      }
      this.fillRecipes();
      this.render();
    }

    // Recipe lists for Terrine (dogs) and fillets, the product page's own recipe preselected.
    fillRecipes() {
      const { pet } = this.state;
      const presetId = Number(this.dataset.presetId);
      [
        [this.el.terrineRecipe, this.list('terrines', pet)],
        [this.el.filletRecipe, this.list('fillets', pet)],
      ].forEach(([select, recipes]) => {
        select.replaceChildren(...recipes.map((r) => this.option(r.id, r.name)));
        const preset = recipes.find((r) => r.id === presetId);
        if (preset) select.value = preset.id;
      });
      // Starting amounts: on a Terrine page half Terrine, on a fillet page 1 tin, otherwise what fits the pet.
      const range = this.dataset.presetRange;
      if (this.state.terrine === null) this.state.terrine = pet === 'dog' && range !== 'fillets' ? 0.5 : 0;
      if (this.state.fillet === null) this.state.fillet = pet === 'cat' || range === 'fillets' ? 1 : 0;
    }

    kibble() {
      return this.products.find((p) => String(p.id) === this.el.kibble.value) || null;
    }

    kibbleKcal() {
      const k = this.kibble();
      return k ? k.kcal : OTHER_KCAL;
    }

    energyFromWeight() {
      const w = parseFloat(String(this.el.weight.value).replace(',', '.'));
      if (!w || w <= 0) return 0;
      const f = { calm: 0, normal: 1, active: 2 }[this.state.activity];
      // Matched to the Kookut kibble labels: dogs "less active / active" about 80 / 110 kcal x kg^0.75,
      // cats "indoor / out and indoor" about 90 / 110 kcal x kg^0.67.
      return this.state.pet === 'dog' ? [80, 95, 110][f] * Math.pow(w, 0.75) : [85, 95, 115][f] * Math.pow(w, 0.67);
    }

    round5(g) {
      return Math.max(0, Math.round(g / 5) * 5);
    }

    tins(n) {
      const whole = Math.floor(n);
      const frac = FRACTIONS[Math.round((n - whole) * 4) / 4] || '';
      const num = (whole ? String(whole) : '') + frac || '0';
      return `${num} ${n > 1 ? this.t.tins : this.t.tin}`;
    }

    fill(str, vars) {
      return String(str || '').replace(/\{(\w+)\}/g, (_, k) => (vars[k] !== undefined ? vars[k] : ''));
    }

    // One result line: optional tin/bag photo, the amount, and an optional small second line
    // ("instead of 50 g") that always sits on its own line so it never breaks mid-phrase on phones.
    li(text, sub, img) {
      const li = document.createElement('li');
      if (img) {
        const im = document.createElement('img');
        im.src = img;
        im.alt = '';
        im.width = 56;
        im.height = 56;
        li.appendChild(im);
        li.classList.add('kk-calc__line--img');
      }
      const body = document.createElement('div');
      body.textContent = text;
      if (sub) {
        const s = document.createElement('span');
        s.textContent = sub;
        body.appendChild(s);
      }
      li.appendChild(body);
      return li;
    }

    amountButtons(container, key, values, labels) {
      if (!values.includes(this.state[key])) this.state[key] = values[0];
      container.replaceChildren(
        ...values.map((v, i) => {
          const b = document.createElement('button');
          b.type = 'button';
          b.setAttribute('role', 'radio');
          b.setAttribute('aria-checked', String(v === this.state[key]));
          b.textContent = labels[i];
          b.addEventListener('click', () => {
            this.state[key] = v;
            this.render();
          });
          return b;
        })
      );
    }

    // Calorie split of the day as a bar with a legend (kibble / Terrine / fillet), in the tin colours.
    drawBar(parts) {
      const total = parts.reduce((a, p) => a + p.kcal, 0);
      if (!total || parts.length < 2) {
        this.el.bar.replaceChildren();
        this.el.legend.replaceChildren();
        this.el.bar.hidden = true;
        this.el.legend.hidden = true;
        return;
      }
      this.el.bar.hidden = false;
      this.el.legend.hidden = false;
      this.el.bar.replaceChildren(
        ...parts.map((p) => {
          const s = document.createElement('span');
          s.style.width = `${(p.kcal / total) * 100}%`;
          s.style.background = p.color;
          return s;
        })
      );
      this.el.legend.replaceChildren(
        ...parts.map((p) => {
          const li = document.createElement('li');
          const dot = document.createElement('i');
          dot.style.background = p.color;
          li.append(dot, `${p.label} ${Math.round((p.kcal / total) * 100)} %`);
          return li;
        })
      );
    }

    render() {
      const { pet, useWeight, activity } = this.state;
      const t = this.t;
      this.querySelectorAll('[data-kk-pet]').forEach((b) => b.setAttribute('aria-checked', String(b.dataset.kkPet === pet)));
      this.querySelectorAll('[data-kk-activity]').forEach((b) =>
        b.setAttribute('aria-checked', String(b.dataset.kkActivity === activity))
      );
      const terrines = this.list('terrines', pet);
      this.el.terrineBlock.hidden = pet !== 'dog' || !terrines.length;
      if (this.el.terrineBlock.hidden) this.state.terrine = 0;
      this.el.gramsWrap.hidden = useWeight;
      this.el.weightWrap.hidden = !useWeight;

      this.amountButtons(this.el.terrineAmounts, 'terrine', [0, 0.25, 0.5, 0.75, 1], [t.none, '¼', '½', '¾', t.all]);
      const filletValues = pet === 'cat' ? [0, 0.5, 1, 1.5, 2] : [0, 0.5, 1, 2];
      this.amountButtons(
        this.el.filletAmounts,
        'fillet',
        filletValues,
        filletValues.map((v) => (v ? this.tins(v).split(' ')[0] : t.none))
      );

      const kcalKibble = this.kibbleKcal();
      let energy;
      let gramsNow;
      if (useWeight) {
        energy = this.energyFromWeight();
        gramsNow = this.round5((energy / kcalKibble) * 100);
      } else {
        gramsNow = parseFloat(this.el.grams.value) || 0;
        energy = (gramsNow * kcalKibble) / 100;
      }
      if (!energy) {
        this.el.lines.replaceChildren(this.li(t.enterAmount));
        this.el.cost.textContent = '';
        this.el.note.textContent = '';
        this.drawBar([]);
        return;
      }

      const byId = (sel) => this.products.find((p) => String(p.id) === sel.value);
      const tinInfo = (recipe, fallbackG) => {
        const g = recipe.tinG || fallbackG;
        return { kcal: (recipe.kcal * g) / 100, cost: recipe.perGram ? recipe.perGram * g : recipe.tinPrice };
      };

      // Fillets first (they sit on top), then Terrine takes its share of what is left, kibble the rest.
      let rest = energy;
      let cost = 0;
      let costKnown = true;
      const lines = [];
      const notes = [];

      const parts = [];
      const fillet = this.state.fillet ? byId(this.el.filletRecipe) : null;
      if (fillet) {
        const f = tinInfo(fillet, pet === 'dog' ? 90 : 70);
        parts.push({ key: 'fillet', kcal: this.state.fillet * f.kcal, color: fillet.color || '#2f3354', label: t.shareFillet });
        rest -= this.state.fillet * f.kcal;
        if (rest <= 0) {
          this.el.lines.replaceChildren(this.li(t.tooMuch));
          this.el.cost.textContent = '';
          this.el.note.textContent = '';
          this.drawBar([]);
          return;
        }
        cost += this.state.fillet * f.cost;
      }

      let terrineTins = 0;
      const terrine = this.state.terrine ? byId(this.el.terrineRecipe) : null;
      if (terrine) {
        const tr = tinInfo(terrine, 400);
        terrineTins = Math.max(0.25, Math.round(((rest * this.state.terrine) / tr.kcal) * 4) / 4);
        if (this.state.terrine < 1) terrineTins = Math.min(terrineTins, Math.floor((rest / tr.kcal) * 4) / 4);
        rest -= terrineTins * tr.kcal;
        cost += terrineTins * tr.cost;
        parts.push({ key: 'terrine', kcal: terrineTins * tr.kcal, color: terrine.color || '#991647', label: t.shareTerrine });
      }

      const kibbleG = this.state.terrine === 1 ? 0 : this.round5((Math.max(0, rest) / kcalKibble) * 100);
      const kib = this.kibble(); // null for another brand: no photo, no price
      if (kibbleG > 0) {
        parts.unshift({ key: 'kibble', kcal: (kibbleG * kcalKibble) / 100, color: '#d8ccb8', label: t.shareKibble });
        lines.push(
          this.li(
            this.fill(t.kibble, { g: kibbleG }),
            gramsNow && kibbleG !== this.round5(gramsNow) ? this.fill(t.instead, { g: this.round5(gramsNow) }) : '',
            kib ? kib.img : ''
          )
        );
        if (kib && kib.perGram) cost += kib.perGram * kibbleG;
        else costKnown = false;
      }
      if (terrine && terrineTins)
        lines.push(this.li(this.fill(t.terrine, { amount: this.tins(terrineTins), name: terrine.name }), '', terrine.img));
      if (fillet) lines.push(this.li(this.fill(t.fillet, { amount: this.tins(this.state.fillet), name: fillet.name }), '', fillet.img));

      if (terrine) notes.push(this.state.terrine === 1 ? t.noteTerrineAll : t.noteTerrine);
      if (fillet) {
        notes.push(t.noteFillet);
        if (pet === 'cat') notes.push(t.noteCatLabel);
      }
      if (useWeight) notes.push(t.noteEstimate);

      this.el.lines.replaceChildren(...lines);
      // Bar order follows the bowl: kibble, then Terrine, fillet on top.
      const order = { kibble: 0, terrine: 1, fillet: 2 };
      this.drawBar(parts.sort((a, b) => order[a.key] - order[b.key]));
      const price = this.money.format(cost / 100);
      this.el.cost.textContent = cost ? (costKnown ? this.fill(t.cost, { price }) : this.fill(t.costFillets, { price })) : '';
      this.el.note.textContent = notes.join(' ');
    }
  }

  customElements.define('kk-feed-calc', KkFeedCalc);
}

/* Opens a calculator placed in a <dialog data-kk-calc-dialog> (homepage band button), or shows/hides
   the inline panel on product pages ([data-kk-calc-toggle] in "How to serve"). */
document.addEventListener('click', (e) => {
  const toggle = e.target.closest('[data-kk-calc-toggle]');
  if (toggle) {
    const panel = document.getElementById(toggle.getAttribute('aria-controls'));
    if (!panel) return;
    panel.hidden = !panel.hidden;
    document
      .querySelectorAll(`[data-kk-calc-toggle][aria-controls="${panel.id}"][aria-expanded]`)
      .forEach((b) => b.setAttribute('aria-expanded', String(!panel.hidden)));
    if (!panel.hidden) panel.scrollIntoView({ behavior: 'smooth', block: 'start' });
    return;
  }
  const opener = e.target.closest('[data-kk-calc-open]');
  if (opener) {
    const dialog = document.getElementById(opener.getAttribute('aria-controls'));
    if (dialog && dialog.showModal) {
      dialog.showModal();
      opener.setAttribute('aria-expanded', 'true');
    }
    return;
  }
  const closer = e.target.closest('[data-kk-calc-close]');
  if (closer) {
    const dialog = closer.closest('dialog');
    if (dialog) dialog.close();
    return;
  }
  // Click on the backdrop closes the dialog.
  if (e.target.matches && e.target.matches('dialog[data-kk-calc-dialog]')) e.target.close();
});

// A link ending in #calculator (e.g. /collections/for-cats#calculator, from a Journal article)
// opens the feeding calculator on arrival.
function kkOpenCalcFromHash() {
  if (window.location.hash !== '#calculator') return;
  const opener = document.querySelector('[data-kk-calc-open]');
  if (opener) opener.click();
}
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', kkOpenCalcFromHash);
} else {
  kkOpenCalcFromHash();
}
window.addEventListener('hashchange', kkOpenCalcFromHash);
